import { useAuth0 } from '@auth0/auth0-react'
import useSWR from 'swr'
import { fetchTodayModule, TodayAuthError, type ModuleId, type TodayModuleDataMap } from '@/lib/bff/today'
import { TODAY_POLL_MS } from '@/hooks/use-today-context'

/*
 * Loads a single Today module's data, independently of every other module, so
 * one module's failure surfaces its own inline error/retry without blocking the
 * rest. Keyed by module + tenant + venue so switching scope refetches, and
 * polls every 60s so a changed value can animate in place. Retries are manual
 * (an error surfaces a retry control) rather than automatic.
 */
export function useTodayModule<M extends ModuleId>(
  module: M,
  tenantId: string | null,
  venueId: string,
  enabled = true,
) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()

  const { data, error, isLoading, mutate } = useSWR<TodayModuleDataMap[M]>(
    isAuthenticated && tenantId && enabled ? ['bff/today/module', module, tenantId, venueId] : null,
    async ([, mod, id, venue]: [string, M, string, string]) => {
      const token = await getAccessTokenSilently()
      if (!token) throw new TodayAuthError('No access token available')
      return fetchTodayModule(mod, token, id, venue)
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      refreshInterval: TODAY_POLL_MS,
    },
  )

  return {
    data,
    error,
    isLoading,
    retry: () => mutate(),
  }
}
